import type { SubscriptionFeature } from "@/schemas/subscription-limits.schema";
import type { UserProfile } from "@/schemas/user-profile.schema";
import { acceptHMRUpdate, defineStore } from "pinia";
import { ref } from "vue";

/** Set to `true` to re-enable subscription/billing/upgrade UI. */
export const SUBSCRIPTIONS_ENABLED = false;

export const useUserProfile = defineStore("user", () => {
  const user = ref<UserProfile | null>(null);

  return {
    user,
    setUser(newUser: UserProfile) {
      user.value = newUser;
    },
  };
});

export function hasRole(roles: string) {
  const profile = useUserProfile().user;

  if (!profile) return false;

  const patterns = roles.split("|"); // Split the pattern by |

  return patterns.some((pattern) => {
    if (pattern.startsWith("!")) {
      return profile.role !== pattern.slice(1);
    }

    return profile.role === pattern;
  });
}

export function hasOrganizationScope(): boolean {
  return useUserProfile().user?.role_scope === "organization";
}

/**
 * Checks if the current user has a specific permission.
 * Supports wildcards: *, resource:*, resource:action
 *
 * @param permission - The permission to check (e.g., "agents:create")
 * @returns true if user has the permission, false otherwise
 *
 * @example
 * hasPermission("agents:create") // exact match
 * hasPermission("agents:delete") // matches if user has "agents:*" or "*"
 */
export function hasPermission(permission: string): boolean {
  const profile = useUserProfile().user;

  if (!profile?.permissions) return false;

  const userPermissions = profile.permissions;

  // Super wildcard - grants all permissions
  if (userPermissions.includes("*")) return true;

  // Resource wildcard - e.g., "agents:*" matches "agents:create"
  const [resource] = permission.split(":");
  if (userPermissions.includes(`${resource}:*`)) return true;

  // Exact match
  return userPermissions.includes(permission);
}

/**
 * Checks if the current user has ANY of the specified permissions (OR logic).
 * Returns true if at least one permission matches.
 *
 * @param permissions - The permissions to check
 * @returns true if user has at least one of the permissions, false otherwise
 *
 * @example
 * hasAnyPermission("agents:create", "agents:update") // true if user can create OR update
 * hasAnyPermission("billing:read", "billing:manage") // true if user can read OR manage billing
 */
export function hasAnyPermission(...permissions: string[]): boolean {
  if (permissions.length === 0) return false;
  return permissions.some(hasPermission);
}

/**
 * Checks if the current user has ALL of the specified permissions (AND logic).
 * Returns true only if all permissions match.
 *
 * @param permissions - The permissions to check
 * @returns true if user has all of the permissions, false otherwise
 *
 * @example
 * hasAllPermissions("agents:read", "agents:create") // true only if user can read AND create
 * hasAllPermissions("members:read", "billing:read") // true only if user can view both members and billing
 */
export function hasAllPermissions(...permissions: string[]): boolean {
  if (permissions.length === 0) return true;
  return permissions.every(hasPermission);
}

/**
 * Checks if the organization's subscription has a specific feature enabled.
 * Uses the features array from the /me endpoint (subscription.features).
 *
 * Independent of SUBSCRIPTIONS_ENABLED: that flag only hides the upgrade UI.
 * The server enforces these features (403 feature_not_available), so the
 * client must hide what the plan does not grant instead of assuming true.
 */
export function hasSubscriptionFeature(feature: SubscriptionFeature): boolean {
  const profile = useUserProfile().user;
  if (!profile) return false;
  return profile.subscription?.features?.includes(feature) ?? false;
}

const _features = [
  // "audio-enhancement",
  "escalate_to_human",
  "whatsapp_baileys",
] as const;
type Feature = (typeof _features)[number];

export function hasFeatureEnabled(feature: Feature): boolean {
  const profile = useUserProfile().user;

  if (!profile) return false;

  return profile.features?.some((f) => f.name === feature) ?? false;
}

export function getFeatureProps(feature: Feature): {
  isBeta: boolean;
  isDeprecated: boolean;
} {
  const profile = useUserProfile().user;
  const featureData = profile?.features?.find((f) => f.name === feature);

  if (!featureData) return { isBeta: false, isDeprecated: false };
  return {
    isBeta: featureData.is_beta ?? false,
    isDeprecated: featureData.is_deprecated ?? false,
  };
}

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserProfile, import.meta.hot));
}
